import { logger } from '@talos/kyoko';

export interface IFrameTokenMessage {
  type: 'ACCESS_TOKEN';
  token: string;
}

/**
 * Sends a message to parent window requesting access token
 * This can be used if the parent hasn't automatically sent the token
 */
export function requestTokenFromParent(): void {
  try {
    window.parent.postMessage({ type: 'REQUEST_ACCESS_TOKEN' }, '*');
    logger.info('Requested access token from parent window');
  } catch (error) {
    logger.warn('Error requesting token from parent:', { error });
  }
}

/**
 * Waits for an access token to be passed to the IFrame via PostMessage from parent window
 */
export function waitForIFrameToken(timeoutMs: number = 30000): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    let messageListener: ((event: MessageEvent) => void) | null = null;

    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (messageListener) {
        window.removeEventListener('message', messageListener);
        messageListener = null;
      }
    };

    const resolveToken = (token: string) => {
      cleanup();
      resolve(token);
    };

    const rejectToken = (error: Error) => {
      cleanup();
      reject(error);
    };

    // Set up timeout
    timeoutId = setTimeout(() => {
      const error = new Error('Timeout waiting for access token in IFrame');
      logger.error(error);
      rejectToken(error);
    }, timeoutMs);

    // Set up PostMessage listener
    messageListener = (event: MessageEvent) => {
      try {
        // Log origin for debugging
        logger.info('PostMessage received', { origin: event.origin, type: event.data?.type });

        // Validate message structure
        if (event.data && typeof event.data === 'object' && event.data.type === 'ACCESS_TOKEN') {
          const message = event.data as IFrameTokenMessage;

          if (message.token && typeof message.token === 'string') {
            logger.info('Access token received via PostMessage from parent window', {
              origin: event.origin,
            });
            resolveToken(message.token);
          } else {
            logger.warn('Invalid token in PostMessage', { hasToken: !!message.token });
          }
        }
      } catch (error) {
        logger.warn('Error processing PostMessage:', { error });
      }
    };

    window.addEventListener('message', messageListener);
  });
}
